import React from 'react';
import { formatCurrencyAmount } from '../../../utils/currency';

const formatCurrencyNoDecimals = (value, currencyCode = 'USD') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return formatCurrencyAmount(Math.round(n), currencyCode, {
    locale: 'es-ES',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    display: 'code',
  });
};

const formatPercent0 = (value) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * 100)}%`;
};

const formatPercent2 = (value) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(2)}%`;
};

export function isPlacementsSubItems(subItems) {
  if (!Array.isArray(subItems) || subItems.length === 0) return false;
  return subItems.some((it) => {
    if (!it || typeof it !== 'object') return false;
    const typeMatches = String(it.type || '').toLowerCase() === 'placement';
    const hasSpendShare = it.metrics && it.metrics.spend_share !== null && it.metrics.spend_share !== undefined;
    return typeMatches || hasSpendShare;
  });
}

export default function PlacementsSubItemsTable({
  subItems,
  subItemsSummary,
  currency = 'USD',
  selectedIds = null,
  onToggle = null,
}) {
  if (!Array.isArray(subItems) || subItems.length === 0) return null;
  const summary = subItemsSummary && typeof subItemsSummary === 'object' ? subItemsSummary : null;
  const rows = subItems.slice(0, 8);
  const selectable = Array.isArray(selectedIds) && typeof onToggle === 'function';
  const selectedSet = selectable ? new Set(selectedIds.map((x) => String(x))) : null;
  const selectedCount = selectable ? selectedSet.size : null;
  const totalCount = summary?.total_count ?? rows.length;

  return (
    <div className="mt-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs tracking-wide text-white/60 font-semibold">Placements</div>
          <div className="mt-0.5 text-[11px] text-white/55">
            {selectable
              ? 'Tildá los placements que querés aplicar.'
              : 'Mix actual vs distribución esperada con Advantage+.'}
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center rounded-full border border-white/10 bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white/80 whitespace-nowrap">
          {selectable ? `${selectedCount}/${totalCount} seleccionados` : `${totalCount} placements`}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-8 gap-3 text-[10px] uppercase tracking-wider text-white/45">
        <div className="col-span-2 truncate">Placement</div>
        <div className="text-right">Gasto</div>
        <div className="text-right">Share</div>
        <div className="text-right">CPA</div>
        <div className="text-right">CPA hist.</div>
        <div className="text-right">CTR</div>
        <div className="text-right">Esperado</div>
      </div>

      <div className="mt-2 space-y-2">
        {rows.map((it, idx) => {
          const name = it?.name || 'Placement';
          const m = it?.metrics || {};
          const itemId = it?.id != null ? String(it.id) : null;
          const isChecked = selectable && itemId != null
            ? selectedSet.has(itemId)
            : (it?.selected_by_default !== false);
          return (
            <div key={`${itemId || name}-${idx}`} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
              <div className="grid grid-cols-8 gap-3 items-center">
                <div className="col-span-2 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {selectable && itemId != null ? (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggle(itemId)}
                        aria-label={`Incluir ${name}`}
                        className="h-4 w-4 shrink-0 rounded border-white/30 bg-white/5 accent-emerald-500 cursor-pointer"
                      />
                    ) : null}
                    <div className="text-sm font-semibold text-white truncate" title={name}>
                      {name}
                    </div>
                  </div>
                </div>
                <div className="text-right text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                  {formatCurrencyNoDecimals(m.spend, currency)}
                </div>
                <div className="text-right text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                  {formatPercent0(m.spend_share)}
                </div>
                <div className="text-right text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                  {formatCurrencyNoDecimals(m.cpa, currency)}
                </div>
                <div className="text-right text-sm font-semibold text-white/80 tabular-nums whitespace-nowrap">
                  {formatCurrencyNoDecimals(m.historical_cpa, currency)}
                </div>
                <div className="text-right text-sm font-semibold text-white/80 tabular-nums whitespace-nowrap">
                  {formatPercent2(m.ctr)}
                </div>
                <div className="text-right text-sm font-semibold text-emerald-100 tabular-nums whitespace-nowrap">
                  {formatPercent0(m.expected_share)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
